// src/components/admin/UserForm.jsx
import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useRoles } from '../../hooks/useRoles';
import PasswordStrengthMeter from '../common/PasswordStrengthMeter';
import {
  Button,
  Checkbox,
  Field,
  IconButton,
  ModalActions,
  Select,
  Spinner,
  TextInput,
} from '../common/ui';

// Kept in step with backend/src/utils/passwordValidator.js.
const passwordProblems = (password) => {
  const problems = [];
  if (password.length < 8) problems.push('at least 8 characters');
  if (!/[A-Z]/.test(password)) problems.push('an uppercase letter');
  if (!/[a-z]/.test(password)) problems.push('a lowercase letter');
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) problems.push('a special character');
  return problems;
};

const UserForm = ({ user, onSubmit, onCancel, isSaving }) => {
  const { roles, isLoading } = useRoles();
  const isNew = !user?._id;

  const [form, setForm] = useState({
    username: user?.username || '',
    email: user?.email || '',
    password: '',
    role: user?.role?._id || user?.role || '',
  });
  const [microsoftOnly, setMicrosoftOnly] = useState(user?.authProvider === 'microsoft');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  // Only nag about the password once there's something to check.
  const problems = form.password ? passwordProblems(form.password) : [];
  const needsPassword = !microsoftOnly && (isNew || form.password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (microsoftOnly && !form.email.trim()) {
      setError('Microsoft sign-in matches people by email, so an email is required.');
      return;
    }

    if (needsPassword && passwordProblems(form.password).length > 0) {
      setError('That password doesn’t meet the requirements yet.');
      return;
    }

    try {
      await onSubmit({
        username: form.username.trim(),
        email: form.email.trim(),
        role: form.role,
        ...(microsoftOnly ? {} : form.password ? { password: form.password } : {}),
      });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Could not save this person');
    }
  };

  if (isLoading) return <Spinner label="Loading roles…" />;

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {error && (
        <p className="rounded-lg bg-danger-soft px-3 py-2.5 text-[13px] text-danger">{error}</p>
      )}

      <Field label="Username" htmlFor="user-name">
        <TextInput
          id="user-name"
          value={form.username}
          onChange={(e) => set({ username: e.target.value })}
          placeholder="priya.n"
          required
        />
      </Field>

      <Field
        label="Email"
        htmlFor="user-email"
        hint="Microsoft sign-in matches people by this address."
      >
        <TextInput
          id="user-email"
          type="email"
          value={form.email}
          onChange={(e) => set({ email: e.target.value })}
          placeholder="priya.n@bestinbrands.com"
          required={microsoftOnly}
        />
      </Field>

      <Checkbox
        id="microsoft-only"
        checked={microsoftOnly}
        onChange={(e) => {
          setMicrosoftOnly(e.target.checked);
          if (e.target.checked) set({ password: '' });
        }}
        label="Microsoft sign-in only"
        description="No password. They sign in with their work account."
      />

      {!microsoftOnly && (
        <Field
          label={isNew ? 'Password' : 'New password'}
          htmlFor="user-password"
          hint={isNew ? undefined : 'Leave blank to keep their current one.'}
          error={
            problems.length > 0 ? `Still needs ${problems.join(', ')}.` : ''
          }
        >
          <div className="relative">
            <TextInput
              id="user-password"
              type={showPassword ? 'text' : 'password'}
              value={form.password}
              onChange={(e) => set({ password: e.target.value })}
              invalid={problems.length > 0}
              className="pr-11"
              required={isNew}
              autoComplete="new-password"
            />
            <IconButton
              label={showPassword ? 'Hide password' : 'Show password'}
              onClick={() => setShowPassword((s) => !s)}
              className="absolute right-1 top-1/2 -translate-y-1/2"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </IconButton>
          </div>
          {form.password && <PasswordStrengthMeter password={form.password} />}
        </Field>
      )}

      <Field label="Role" htmlFor="user-role" hint="Decides which tools they see.">
        <Select
          id="user-role"
          value={form.role}
          onChange={(e) => set({ role: e.target.value })}
          required
        >
          <option value="">Choose a role…</option>
          {roles.map((role) => (
            <option key={role._id} value={role._id}>
              {role.name}
            </option>
          ))}
        </Select>
      </Field>

      <ModalActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button type="submit" variant="primary" disabled={isSaving}>
          {isSaving ? 'Saving…' : isNew ? 'Add person' : 'Save changes'}
        </Button>
      </ModalActions>
    </form>
  );
};

export default UserForm;
